import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { publicHttp, setAuthToken } from "~/config/axios";
import type { BaseApiResponse } from "~/types/common";
import { setTokens } from "%/token";

export const Route = createFileRoute("/auth/handoff")({
  component: AuthHandoff,
});

interface HandoffRedeemResponse {
  accessToken: string;
  refreshToken: string;
  path: string;
  userType?: string;
  fromApp?: boolean;
}

const unwrap = <T,>(payload: BaseApiResponse<T> | T | undefined): T => {
  if (payload == null) throw new Error("응답이 비어있습니다");
  if (typeof payload === "object" && payload !== null && "data" in (payload as any)) {
    return (payload as BaseApiResponse<T>).data;
  }
  return payload as T;
};

const safePath = (path: string | null | undefined): string => {
  if (!path || !path.startsWith("/") || path.startsWith("//")) {
    return "/";
  }
  return path;
};

function AuthHandoff() {
  const { history } = useRouter();
  const calledRef = useRef(false);
  const [message, setMessage] = useState("앱 로그인 정보를 확인하고 있어요...");

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const redeem = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        const fallbackPath = safePath(params.get("path"));

        if (!token) {
          throw new Error("핸드오프 토큰이 없습니다");
        }

        const { data } = await publicHttp.post<BaseApiResponse<HandoffRedeemResponse>>(
          "/auth/handoff/redeem",
          { token },
        );
        const redeemed = unwrap(data);

        if (!redeemed.accessToken || !redeemed.refreshToken) {
          throw new Error("인증 토큰이 비어있습니다");
        }

        setTokens(redeemed.accessToken, redeemed.refreshToken);
        setAuthToken(redeemed.accessToken);

        if (redeemed.fromApp || params.get("fromApp") === "1") {
          sessionStorage.setItem("mung:fromApp", "1");
        }

        history.replace(safePath(redeemed.path || fallbackPath));
      } catch (e) {
        console.error("[handoff] redeem failed:", e);
        setMessage("로그인 정보를 넘겨받지 못했어요. 앱에서 다시 시도해 주세요.");
      }
    };

    void redeem();
  }, [history]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F5F5F5] px-6">
      <div className="text-center text-sm font-medium text-gray-600">{message}</div>
    </div>
  );
}
